import { type ReactNode, useEffect, useId, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from './Button';
import { registerModalHost } from './toast-store';
import styles from './ui.module.css';

export interface DialogProps {
  open: boolean;
  onClose(): void;
  title: string;
  /** sheet: bottom sheet on phones, centred dialog on larger screens. panel: full screen on phones, side panel from tablet up. */
  variant?: 'sheet' | 'panel' | 'alert';
  children: ReactNode;
  footer?: ReactNode;
  /** Hide the visual title (still announced). */
  hideTitle?: boolean;
  headerExtra?: ReactNode;
  /** Wider centred dialog from tablet up (tables such as the sharing table). */
  wide?: boolean;
}

/**
 * Native <dialog> with showModal(): the browser provides the focus trap, inert background,
 * Escape to close and returns focus to the trigger (frontend.md → Accessibility).
 */
export function Dialog({
  open,
  onClose,
  title,
  variant = 'sheet',
  children,
  footer,
  hideTitle,
  headerExtra,
  wide,
}: DialogProps) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const { t } = useTranslation();

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open && !el.open) {
      if (typeof el.showModal === 'function') el.showModal();
      else el.setAttribute('open', '');
    } else if (!open && el.open) {
      if (typeof el.close === 'function') el.close();
      else el.removeAttribute('open');
    }
  }, [open]);

  useEffect(() => {
    const el = ref.current;
    if (!open || !el) return;
    return registerModalHost(el);
  }, [open]);

  return (
    <dialog
      ref={ref}
      className={[styles.dialog, styles[`dialog-${variant}`], wide && styles.dialogWide]
        .filter(Boolean)
        .join(' ')}
      aria-labelledby={titleId}
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
      onClick={(e) => {
        // Click on the backdrop (the dialog element itself, outside its content box) closes.
        if (e.target === ref.current && variant !== 'alert') onClose();
      }}
    >
      {open && (
        <div className={styles.dialogBody}>
          <header className={styles.dialogHeader}>
            <h2 id={titleId} className={hideTitle ? 'visually-hidden' : styles.dialogTitle}>
              {title}
            </h2>
            {headerExtra}
            {variant !== 'alert' && (
              <Button variant="ghost" icon="close" iconOnly onClick={onClose}>
                {t('common.close')}
              </Button>
            )}
          </header>
          <div className={styles.dialogContent}>{children}</div>
          {footer && <footer className={styles.dialogFooter}>{footer}</footer>}
        </div>
      )}
    </dialog>
  );
}
